import * as React from 'react';
import { api, getToken, setToken, clearToken } from '../api-client';
import { clearStashedAdminToken } from './impersonation';

export interface SuperAdminIdentity {
  type: 'super_admin';
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  /** Role keys granted to this admin (e.g. ['owner'] or ['site_manager']). */
  roles?: string[];
  /** Effective permission keys after expansion of owner / role grants. */
  permissions?: string[];
}

export interface UserIdentity {
  type: 'user';
  id: string;
  tenant_id: string;
  email: string;
  name: string;
  is_active: boolean;
  notification_prefs?: Record<string, unknown>;
  /** Effective IANA timezone (user override → tenant default → 'UTC'). */
  timezone?: string;
  /** The tenant's default IANA timezone, if the user hasn't overridden it. */
  tenant_timezone?: string | null;
  /**
   * Effective permission keys for this user within the tenant, from
   * GET /api/auth/me. Owners receive the wildcard `['*']` (mirrors the
   * server-side owner fast-pass in requirePermission).
   */
  permissions?: string[];
  /** True iff the user holds the tenant 'owner' role. */
  is_owner?: boolean;
}

export type Identity = SuperAdminIdentity | UserIdentity;

/**
 * A password-verified (or magic-link-redeemed) login that still needs a
 * second factor. Carries a short-lived `mfa_token` — NOT a session — that
 * must be exchanged via `verifyMfa` before any identity is adopted.
 */
export interface MfaChallenge {
  mfa_required: true;
  mfa_token: string;
}

function isMfaChallenge(v: unknown): v is MfaChallenge {
  return !!v && typeof v === 'object' && (v as any).mfa_required === true;
}

/**
 * A super-admin login blocked by the platform's `require_admin_mfa` toggle:
 * the password was correct, but this admin hasn't enrolled TOTP yet. Carries
 * a short-lived `enroll_token` (NOT a session) that authorizes exactly the
 * enroll → confirm round trip — no session is issued until that completes
 * and the admin logs in again.
 */
export interface MfaEnrollmentRequired {
  mfa_enrollment_required: true;
  enroll_token: string;
}

function isMfaEnrollmentRequired(v: unknown): v is MfaEnrollmentRequired {
  return !!v && typeof v === 'object' && (v as any).mfa_enrollment_required === true;
}

/** One workspace offered by the password-login picker. */
export interface LoginWorkspace {
  tenantId: string;
  slug: string;
  tenantName: string;
}

/**
 * The submitted password authenticated in more than one workspace (an email
 * is unique only per tenant). Carries a short-lived `pendingToken` — NOT a
 * session and NOT an MFA token — that must be exchanged via `chooseWorkspace`
 * together with the tenant the user picked.
 */
export interface WorkspaceSelectionRequired {
  workspace_selection_required: true;
  pendingToken: string;
  workspaces: LoginWorkspace[];
}

function isWorkspaceSelection(v: unknown): v is WorkspaceSelectionRequired {
  return !!v && typeof v === 'object' && (v as any).workspace_selection_required === true;
}

interface AuthContextValue {
  identity: Identity | null;
  loading: boolean;
  /**
   * Effective permission keys from /me. Empty while logged out or loading.
   * Contains `'*'` for tenant owners (universal). If the API predates the
   * permissions field (deploy skew), we fail OPEN with `'*'` — the server-side
   * requirePermission middleware remains the real gate.
   */
  permissions: Set<string>;
  login(args: {
    email: string;
    password: string;
    turnstileToken: string;
    type?: 'super_admin' | 'user';
    /** Optional workspace selector — skips the picker when already known. */
    tenantId?: string;
    tenantSlug?: string;
  }): Promise<Identity | MfaChallenge | MfaEnrollmentRequired | WorkspaceSelectionRequired>;
  /** Second step of a login that returned a WorkspaceSelectionRequired. */
  chooseWorkspace(args: { pendingToken: string; tenantId: string }): Promise<Identity | MfaChallenge>;
  redeemMagicLink(args: { token: string; password: string; turnstileToken: string }): Promise<Identity | MfaChallenge>;
  /** Second step of a login/redeem that returned an MfaChallenge. Exchanges the mfa_token + code (or recovery code, or emailed code) for a real session. */
  verifyMfa(args: { mfaToken: string; code?: string; recoveryCode?: string; emailCode?: string }): Promise<Identity>;
  /** Adopt a server-issued JWT (OAuth flows): store it, fetch /me, set identity. */
  adoptToken(token: string): Promise<Identity>;
  logout(opts?: { redirectTo?: string | null }): void;
  refresh(): Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [identity, setIdentity] = React.useState<Identity | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  const refresh = React.useCallback(async () => {
    const token = getToken();
    if (!token) { setIdentity(null); setLoading(false); return; }
    try {
      const me = await api<Identity>('/api/auth/me');
      setIdentity(me);
    } catch {
      setIdentity(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const login = React.useCallback<AuthContextValue['login']>(async ({ email, password, turnstileToken, type, tenantId, tenantSlug }) => {
    const result = await api<{ token: string; admin?: any; user?: any } | MfaChallenge | MfaEnrollmentRequired | WorkspaceSelectionRequired>(
      '/api/auth/login',
      {
        method: 'POST',
        body: { email, password, turnstileToken, type, tenant_id: tenantId, tenant_slug: tenantSlug },
        noRedirectOn401: true,
      },
    );
    if (isMfaChallenge(result)) return result;
    if (isMfaEnrollmentRequired(result)) return result;
    // Ambiguous email: no session was issued, so nothing to adopt yet.
    if (isWorkspaceSelection(result)) return result;
    setToken(result.token);
    const me = await api<Identity>('/api/auth/me');
    setIdentity(me);
    return me;
  }, []);

  const chooseWorkspace = React.useCallback<AuthContextValue['chooseWorkspace']>(async ({ pendingToken, tenantId }) => {
    const result = await api<{ token: string; user: any } | MfaChallenge>(
      '/api/auth/login/workspace',
      { method: 'POST', body: { pendingToken, tenantId }, noRedirectOn401: true },
    );
    // The picker never bypasses the second factor: a TOTP account still lands here.
    if (isMfaChallenge(result)) return result;
    setToken(result.token);
    const me = await api<Identity>('/api/auth/me');
    setIdentity(me);
    return me;
  }, []);

  const redeemMagicLink = React.useCallback<AuthContextValue['redeemMagicLink']>(async ({ token, password, turnstileToken }) => {
    const result = await api<{ token: string; user: any } | MfaChallenge>(
      '/api/auth/magic-link/redeem',
      { method: 'POST', body: { token, password, turnstileToken }, noRedirectOn401: true },
    );
    if (isMfaChallenge(result)) return result;
    setToken(result.token);
    const me = await api<Identity>('/api/auth/me');
    setIdentity(me);
    return me;
  }, []);

  const verifyMfa = React.useCallback<AuthContextValue['verifyMfa']>(async ({ mfaToken, code, recoveryCode, emailCode }) => {
    const result = await api<{ token: string; user?: any; admin?: any }>(
      '/api/auth/mfa/verify',
      { method: 'POST', body: { mfa_token: mfaToken, code, recoveryCode, email_code: emailCode }, noRedirectOn401: true },
    );
    setToken(result.token);
    const me = await api<Identity>('/api/auth/me');
    setIdentity(me);
    return me;
  }, []);

  const adoptToken = React.useCallback<AuthContextValue['adoptToken']>(async (token) => {
    setToken(token);
    const me = await api<Identity>('/api/auth/me');
    setIdentity(me);
    return me;
  }, []);

  const logout = React.useCallback<AuthContextValue['logout']>((opts) => {
    clearToken();
    clearStashedAdminToken();
    setIdentity(null);
    // Hard-navigate by default so no stale dashboard data stays visible and
    // in-flight API calls can't repopulate state. Pass { redirectTo: null }
    // to opt out (e.g. inline error handling on the login page itself).
    const dest = opts?.redirectTo === undefined ? '/login' : opts.redirectTo;
    if (dest) window.location.replace(dest);
  }, []);

  const permissions = React.useMemo<Set<string>>(() => {
    if (!identity) return new Set();
    if (identity.type === 'super_admin') return new Set(identity.permissions ?? []);
    // Tenant user. An older API build won't include the field — fail open
    // (hide nothing); the backend requirePermission gate still enforces.
    if (identity.permissions === undefined) return new Set(['*']);
    return new Set(identity.permissions);
  }, [identity]);

  const value: AuthContextValue = { identity, loading, permissions, login, chooseWorkspace, redeemMagicLink, verifyMfa, adoptToken, logout, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = React.useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
